import type { FC } from 'react';
import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import cn from 'classnames';
import { useForm } from 'react-final-form';
import { sendEvent } from '@alfa-bank/site-helpers-common/browser/send-event';
import type { IOnChangeCallback } from '@alfa-bank/site-tabs/src/models';
import type {
    ITimeSlotsAvailableDate,
    TDeliveryType,
} from '@alfa-bank/site-dc-form-api/src/models';
import { bookTimeSlotRequest, selectDeliveryData } from '@alfa-bank/site-dc-form-api/store';

import { deliveryStepAnalytics as analytics } from '../../../../analytics';

import type { TDeliveryForm } from './models';

export const DeliveryForm: FC<TDeliveryForm> = ({
    DaySlots,
    NoDeliveryBlock,
    OfficeDelivery,
    Tab,
    TabsTag,
    Text,
    alertText,
    className,
    comment,
    courierFormSettings,
    isFirstTimeSlotPreSelected,
    marginsPreset,
    officeDeliveryType,
    offices,
    selectedDate: dateSettings,
    selectedOffice,
    selectedTime,
    styles,
    textPreset,
    timeSlots,
}) => {
    const formApi = useForm();
    const dispatch = useDispatch();
    const { isNeedReselectTimeSlot } = useSelector(selectDeliveryData);

    const [selectedDate, setSelectedDate] = useState<ITimeSlotsAvailableDate | null>(null);

    const timeSlotsDate = useMemo(() => timeSlots?.map(slot => slot.date), [timeSlots]);

    /** Проверяет что доступно несколько вариантов доставки */
    const isSeveralDeliveryTypesAvailable = useMemo(
        () => [offices, timeSlots].filter(Boolean).length > 1,
        [offices, timeSlots]
    );

    const initialDeliveryType: TDeliveryType = useMemo(
        () => (timeSlots ? 'courier' : 'offices'),
        [timeSlots]
    );

    useEffect(() => {
        if (isNeedReselectTimeSlot) {
            sendEvent(analytics.timeSlots.error);
        }
    }, [isNeedReselectTimeSlot]);

    useEffect(() => {
        if (timeSlots) {
            sendEvent(analytics.timeSlots.view);
        }
    }, [timeSlots]);

    const handleSelectDate = (date: number) => {
        if (!timeSlots) return;

        setSelectedDate(timeSlots[date]);

        formApi.change('selectedTime', undefined);
        formApi.change('selectedDate', date);

        sendEvent(analytics.timeSlots.date.click);
    };

    const handleSelectTime = useCallback(
        (time: number) => {
            if (!timeSlots) return;

            const { selectedDate: selectedDateValue } = formApi.getState().values;
            const selectedDateData = timeSlots[selectedDateValue];
            const { express, slotFrom, slotTo } = selectedDateData.intervals[time];
            const selectedTimeSlot = {
                deliveryDate: selectedDateData?.date,
                slotFrom,
                slotTo,
                express,
            };

            sendEvent(analytics.timeSlots.time.click);

            formApi.change('selectedTime', time.toString());
            dispatch(bookTimeSlotRequest(selectedTimeSlot));
        },
        [dispatch, formApi, timeSlots]
    );

    useEffect(() => {
        formApi.change('selectedDate', 0);
        formApi.change('selectedTime', undefined);
        formApi.change('selectedOffice', []);

        if (timeSlots) {
            const [firstTimeSlot] = timeSlots;
            setSelectedDate(firstTimeSlot);

            const [firstTimeSlotInterval] = firstTimeSlot.intervals;

            if (isFirstTimeSlotPreSelected && firstTimeSlotInterval) {
                handleSelectTime(0);
            }
        }
    }, [formApi, timeSlots, offices, isFirstTimeSlotPreSelected, handleSelectTime]);

    useEffect(() => {
        // TODO: перевести всю работу с deliveryType в редакс
        formApi.change('deliveryType', initialDeliveryType);

        if (offices || timeSlots) {
            sendEvent(
                analytics.deliveryType.view(
                    initialDeliveryType === 'courier' ? 'По адресу' : 'В офис банка'
                )
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialDeliveryType]);

    const handleTabChange = ({ id }: IOnChangeCallback) => {
        formApi.change('deliveryType', id.toString());

        sendEvent(analytics.deliveryType.click(id === 'courier' ? 'По адресу' : 'В офис банка'));
    };

    if (!timeSlots && !offices) {
        return <NoDeliveryBlock alertText={alertText} styler={marginsPreset.alertMargin} />;
    }

    return (
        <div className={className} data-test-id="dc-form-delivery-form">
            {isSeveralDeliveryTypesAvailable && (
                <Text styler={marginsPreset.titleMargin} view={textPreset.title}>
                    Куда доставить
                </Text>
            )}

            <TabsTag
                align="left"
                onChange={handleTabChange}
                selectedID={initialDeliveryType}
                size="xs"
                tabNamesClassName={cn(styles.external, styles.deliveryChips, {
                    [styles.hideChips]: !isSeveralDeliveryTypesAvailable,
                })}
            >
                <Tab hidden={!timeSlots} id="courier" title="По адресу">
                    <>
                        <Text styler={marginsPreset.titleMargin} view={textPreset.title}>
                            {courierFormSettings?.title || 'Дата и время'}
                        </Text>
                        {timeSlotsDate && selectedDate && (
                            <DaySlots
                                comment={comment}
                                dates={timeSlotsDate}
                                intervals={selectedDate?.intervals}
                                onSelectDate={handleSelectDate}
                                onSelectTime={handleSelectTime}
                                selectedDate={dateSettings}
                                selectedTime={selectedTime}
                                showAttention={isNeedReselectTimeSlot}
                            />
                        )}
                    </>
                </Tab>
                <Tab hidden={!offices} id="offices" title="В офис банка">
                    <OfficeDelivery
                        officeDeliveryType={officeDeliveryType}
                        offices={offices}
                        selectedOffice={selectedOffice}
                    />
                </Tab>
            </TabsTag>
        </div>
    );
};
